import { useEffect } from 'react';
import { useTenantMe } from '@/hooks/use-tenant-me';

/**
 * Widget de soporte (webchat) embebido en el panel del contribuyente.
 *
 * El proveedor del webchat es getfish.la (servicio externo de terceros,
 * sin relación con Actuo más allá de ser el vendor del chat). Su script
 * se identifica con los datos reales del contribuyente logueado (de
 * useTenantMe) para que soporte sepa con quién habla sin pedir credenciales.
 *
 * `window.SupportChatSettings` (alias de la global que exige el script del
 * proveedor) es PÚBLICO — va embebido en el cliente, como un Google
 * Analytics ID, no es secreto. Para rotar el token, cambiá la constante
 * o movela a una env var VITE_SUPPORT_WIDGET_TOKEN.
 *
 * NO se carga durante impersonation (admin viendo el panel del tenant) —
 * no queremos abrir sesiones de soporte a nombre del tenant desde el admin.
 *
 * Renderiza null — solo gestiona el side-effect de inyectar el script.
 */

const SUPPORT_WIDGET_TOKEN = 'wc_eca259c079ee9ab06a8d354c1d791676643c71dec62bc6fa';
const SCRIPT_ID = 'support-webchat-widget';
const SUPPORT_WIDGET_HOST = 'https://app.getfish.la';

declare global {
  interface Window {
    // Global requerida por el script del proveedor del webchat.
    FishSettings?: Record<string, unknown>;
  }
}

export function SupportWidget() {
  const { data: me } = useTenantMe();

  useEffect(() => {
    // Sin sesión de tenant, o admin impersonando → no cargar el widget.
    if (!me || me.impersonatedBy) return;

    const tenant = me.tenant;
    const primaryRuc = me.rucs.find((r) => r.isPrimary) ?? me.rucs[0];

    // userPhone: whatsappNumber viene en E.164 sin '+'. Si es placeholder
    // de telegram (no numérico) lo omitimos — el widget espera un teléfono real.
    const phone = /^\d{6,15}$/.test(tenant.whatsappNumber)
      ? `+${tenant.whatsappNumber}`
      : undefined;

    // Identificación — seteamos antes de inyectar el script así el widget
    // la lee al cargar. Actualizar la config en cada cambio de `me`
    // mantiene el contexto fresco si el widget re-lee.
    window.FishSettings = {
      userId: tenant.id,
      userName: tenant.fullName,
      ...(tenant.email ? { userEmail: tenant.email } : {}),
      ...(phone ? { userPhone: phone } : {}),
      company: {
        id: primaryRuc?.ruc ?? tenant.id,
        name: primaryRuc?.razonSocial ?? tenant.displayName,
        plan: me.subscription?.plan ?? 'sin_plan',
      },
      custom: {
        regimen: primaryRuc?.regimen ?? null,
        estado: tenant.status,
        ruc_validado: primaryRuc?.validatedAt ? true : false,
        subscription_status: me.subscription?.status ?? null,
      },
    };

    // Inyectar el script del widget una sola vez por sesión de página.
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement('script');
      s.id = SCRIPT_ID;
      s.src = `${SUPPORT_WIDGET_HOST}/api/webchat/widget.js?token=${SUPPORT_WIDGET_TOKEN}`;
      s.async = true;
      document.body.appendChild(s);
    }
  }, [me]);

  return null;
}
